import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { useConfirm } from '../components/ui/ConfirmDialog.js';
import { useToast } from '../components/ui/Toast.js';
import { parseServerDate } from '../lib/date.js';
import { ApiError } from '../lib/api.js';
import {
  usePendingSignupRequests,
  useApproveSignupRequest,
  useRejectSignupRequest,
  type SignupRequestRow,
} from '../hooks/usePlatformAdmin.js';

function fmtDate(s: string): string {
  return parseServerDate(s).toLocaleString('es', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// Registro público (2026-08-05) — solo is_platform_admin llega hasta acá con
// éxito (403 real en el backend, ver requirePlatformAdmin); esta pantalla
// solo reusa el patrón visual de fila de UsersSection en Settings — sin
// diseño nuevo, sin DataTable, sin sidebar nav nueva (ruta directa).
function RequestRow({ request }: { request: SignupRequestRow }) {
  const { t } = useTranslation();
  const [slug, setSlug] = useState(request.suggestedSlug);
  const approve = useApproveSignupRequest();
  const reject = useRejectSignupRequest();
  const { confirm: confirmAction, dialog: confirmDialog } = useConfirm();
  const toast = useToast();

  async function handleApprove() {
    try {
      await approve.mutateAsync({ id: request.id, slug });
      toast.success(t('platformAdmin.approved', { business: request.businessName }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  async function handleReject() {
    const ok = await confirmAction({ title: t('platformAdmin.rejectConfirm', { business: request.businessName }), tone: 'danger' });
    if (!ok) return;
    try {
      await reject.mutateAsync(request.id);
      toast.success(t('platformAdmin.rejected', { business: request.businessName }));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div
      className="flex items-center justify-between gap-3 px-3 py-2 rounded"
      style={{ background: 'var(--evo-bg-2)', border: '1px solid var(--evo-stroke)' }}
    >
      {confirmDialog}
      <div className="flex flex-col" style={{ minWidth: 0 }}>
        <span className="text-sm font-medium">{request.personName} — {request.businessName}</span>
        <span className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>{request.email} · {fmtDate(request.createdAt)}</span>
      </div>
      <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
        <Input value={slug} onChange={setSlug} size="sm" />
        <Button variant="solid" tone="cyan" size="sm" loading={approve.isPending} onClick={() => { void handleApprove(); }}>
          {t('platformAdmin.approveBtn')}
        </Button>
        <Button variant="outline" tone="muted" size="sm" loading={reject.isPending} onClick={() => { void handleReject(); }}>
          {t('platformAdmin.rejectBtn')}
        </Button>
      </div>
    </div>
  );
}

export function PlatformAdminPage() {
  const { t } = useTranslation();
  const { data, isLoading } = usePendingSignupRequests();
  const requests = data?.requests ?? [];

  return (
    <div style={{ padding: 'var(--evo-space-6) var(--evo-content-pad-x)', maxWidth: '800px' }}>
      <h1 style={{ fontSize: 'var(--evo-fs-h1)', color: 'var(--evo-fg-1)', margin: 0, fontFamily: 'var(--font-display)' }}>
        {t('platformAdmin.title')}
      </h1>
      <p style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-3)', marginTop: 'var(--evo-space-2)', marginBottom: 'var(--evo-space-5)' }}>
        {t('platformAdmin.subtitle')}
      </p>

      {isLoading && <LoadingState />}

      {!isLoading && requests.length === 0 && (
        <EmptyState title={t('platformAdmin.empty')} />
      )}

      {!isLoading && requests.length > 0 && (
        <div className="flex flex-col gap-2">
          {requests.map(r => <RequestRow key={r.id} request={r} />)}
        </div>
      )}
    </div>
  );
}
